import React, { useState, useEffect, useCallback } from "react";
import {
  Page,
  Layout,
  Card,
  TextField,
  Checkbox,
  Button,
  Banner,
  Spinner,
  Text,
  DropZone,
  BlockStack,
  Thumbnail,
  InlineError,
  Box,
} from "@shopify/polaris";
import { useParams, useNavigate } from "react-router-dom";
import { useShop } from "../context/ShopContext.jsx";
import { useI18n } from "../context/I18nContext.jsx";

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function LandingPageEditorPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { apiFetch } = useShop();
  const { t } = useI18n();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [title, setTitle] = useState("");
  const [introText, setIntroText] = useState("");
  const [sizeGuideText, setSizeGuideText] = useState("");
  const [additionalText, setAdditionalText] = useState("");
  const [additionalImages, setAdditionalImages] = useState([]);
  const [showSizeCalculator, setShowSizeCalculator] = useState(true);
  const [orderButtonText, setOrderButtonText] = useState("Bestelling afronden");
  const [orderButtonUrl, setOrderButtonUrl] = useState("");

  const loadPage = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch(`/landing-page/${productId}`);
      setTitle(data.title || "");
      setIntroText(data.introText || "");
      setSizeGuideText(data.sizeGuideText || "");
      setAdditionalText(data.additionalText || "");
      setAdditionalImages(JSON.parse(data.additionalImages || "[]"));
      setShowSizeCalculator(data.showSizeCalculator !== false);
      setOrderButtonText(data.orderButtonText || "Bestelling afronden");
      setOrderButtonUrl(data.orderButtonUrl || "");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const handleAddImage = useCallback(async (_dropped, accepted) => {
    for (const file of accepted) {
      const dataUrl = await fileToBase64(file);
      setAdditionalImages((prev) => [...prev, dataUrl]);
    }
  }, []);

  const handleRemoveImage = (idx) => {
    setAdditionalImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await apiFetch(`/landing-page/${productId}`, {
        method: "PUT",
        body: JSON.stringify({
          title,
          introText,
          sizeGuideText,
          additionalText,
          additionalImages: JSON.stringify(additionalImages),
          showSizeCalculator,
          orderButtonText,
          orderButtonUrl: orderButtonUrl || null,
        }),
      });
      setSuccess(t("landingEditor.saved"));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Page title={t("landingEditor.title", { name: productId })}>
        <Spinner />
      </Page>
    );
  }

  return (
    <Page
      title={t("landingEditor.title", { name: productId })}
      backAction={{ content: t("landingEditor.backAction"), onAction: () => navigate("/products") }}
      primaryAction={{
        content: saving ? t("common.saving") : t("common.save"),
        onAction: handleSave,
        loading: saving,
      }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          </Layout.Section>
        )}
        {success && (
          <Layout.Section>
            <Banner tone="success" onDismiss={() => setSuccess(null)}>
              {success}
            </Banner>
          </Layout.Section>
        )}

        {/* Page title & intro */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">{t("landingEditor.titleSection")}</Text>
            <TextField
              label={t("landingEditor.pageTitle")}
              value={title}
              onChange={setTitle}
              placeholder={t("landingEditor.pageTitlePlaceholder")}
              helpText="Wordt bovenaan de landingspagina weergegeven."
            />
            <br />
            <TextField
              label={t("landingEditor.introText")}
              value={introText}
              onChange={setIntroText}
              multiline={4}
              placeholder={t("landingEditor.introPlaceholder")}
              helpText="Wordt onder de foto's weergegeven."
            />
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        {/* Size guide */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">{t("landingEditor.sizeSection")}</Text>
            <Checkbox
              label={t("landingEditor.showCalculator")}
              checked={showSizeCalculator}
              onChange={setShowSizeCalculator}
            />
            <br />
            <TextField
              label={t("landingEditor.sizeGuide")}
              value={sizeGuideText}
              onChange={setSizeGuideText}
              multiline={4}
              placeholder={t("landingEditor.sizeGuidePlaceholder")}
              helpText="Geef aanvullend advies over maatvoering en pasvorm."
            />
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        {/* Extra content */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">{t("landingEditor.extraSection")}</Text>
            <TextField
              label={t("landingEditor.additionalText")}
              value={additionalText}
              onChange={setAdditionalText}
              multiline={4}
              placeholder={t("landingEditor.additionalTextPlaceholder")}
            />
            <br />
            <Text variant="bodyMd" fontWeight="semibold">
              {t("landingEditor.extraImages")}
            </Text>
            <Text variant="bodySm" tone="subdued" as="p">
              Voeg foto's toe zoals detailfoto's, styling-inspiratie, etc.
            </Text>
            <DropZone accept="image/*" type="image" onDrop={handleAddImage}>
              <DropZone.FileUpload actionTitle={t("landingEditor.addImage")} actionHint={t("landingEditor.dropImage")} />
            </DropZone>
            {additionalImages.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
                {additionalImages.map((src, idx) => (
                  <div key={idx} style={{ position: "relative" }}>
                    <Thumbnail source={src} alt={`Extra afbeelding ${idx + 1}`} size="large" />
                    <button
                      onClick={() => handleRemoveImage(idx)}
                      style={{
                        position: "absolute",
                        top: -6,
                        right: -6,
                        background: "#e44",
                        color: "#fff",
                        border: "none",
                        borderRadius: "50%",
                        width: 20,
                        height: 20,
                        cursor: "pointer",
                        fontSize: 12,
                        lineHeight: "20px",
                        padding: 0,
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        {/* Order button */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">{t("landingEditor.orderSection")}</Text>
            <TextField
              label={t("landingEditor.orderButtonText")}
              value={orderButtonText}
              onChange={setOrderButtonText}
              placeholder={t("landingEditor.orderButtonTextPlaceholder")}
            />
            <br />
            <TextField
              label={t("landingEditor.orderButtonUrl")}
              value={orderButtonUrl}
              onChange={setOrderButtonUrl}
              placeholder="https://uw-winkel.myshopify.com/cart/..."
              helpText={t("landingEditor.orderButtonUrlHelp")}
            />
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
